/**
 * CatchUpStreak — what a student has caught up on.
 *
 * Catching up is invisible work: the lesson is already over and nobody claps.
 * This shows the student it counted.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Flame } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CatchUpStreak = ({ tenantId }) => {
  const { data, isError } = useQuery({
    queryKey: ['catch-up', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/catch-up`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      if (!res.ok) throw new Error('not a student');
      return res.json();
    },
    retry: false,
  });

  // Staff get a 403 here, and a student with nothing to show is not nagged.
  if (isError || !data || data.caught_up === 0) return null;

  return (
    <Card className="p-4 mb-4 border-amber-500/40" data-testid="catch-up-streak">
      <div className="flex flex-wrap items-center gap-3">
        <Flame className="h-5 w-5 text-amber-600" />
        <p className="text-sm">
          You have caught up on{' '}
          <b data-testid="catch-up-count">{data.caught_up}</b>{' '}
          {data.caught_up === 1 ? 'lesson' : 'lessons'} by watching the replay
          {data.hours > 0 && ` — ${data.hours} hours of teaching`}.
        </p>
        {data.weeks_running > 1 && (
          <span className="text-xs rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-1"
                data-testid="catch-up-streak-weeks">
            {data.weeks_running} weeks running
          </span>
        )}
      </div>
    </Card>
  );
};

export default CatchUpStreak;
